/**
 * Product taxonomy (store business lines + structured product types).
 *
 * Mirrors `config/productTaxonomy.js` on the backend: a store declares the
 * lines it sells (clothes, shoes, bags, home…), and each product type lists the
 * attributes a merchant must provide — size, pointure, colour, material.
 */

import apiClient from "../api";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const unwrap = (error: any, fallback: string) =>
  error?.response?.data || error || { message: fallback };

/* --------------------------------------------------------------------- types */

export type AttributeKind = "enum" | "text" | "number";

export interface ProductAttribute {
  key: string;
  label: string;
  kind: AttributeKind;
  options?: string[] | null;
  max?: number;
  min?: number;
  required: boolean;
}

export interface ProductTypeDefinition {
  type: string;
  label: string;
  unitLabel: string;
  required: ProductAttribute[];
  optional: ProductAttribute[];
}

export interface BusinessLineDefinition {
  key: string;
  label: string;
  description: string;
  productTypes: string[];
}

export interface CatalogProfile {
  store: { storeId: string; name: string; category: string };
  businessLines: { key: string; label: string; description?: string }[];
  productTypes: ProductTypeDefinition[];
  summary?: string;
}

export interface ProductFamily {
  productId: string;
  name: string;
  familyName: string;
  productType: string;
  isFamilyHead: boolean;
  attributes: Record<string, string | number>;
  sellPrice: number;
  imageUrl?: string;
  quantityInStock: number;
}

/* --------------------------------------------------------------------- data */

/** The full business-line catalogue, so the store wizard works offline. */
export const BUSINESS_LINES: BusinessLineDefinition[] = [
  {
    key: "FASHION_CLOTHING",
    label: "Vêtements / Mode",
    description: "T-shirts, chemises, pantalons, robes — avec tailles et couleurs.",
    productTypes: ["T_SHIRT", "SHIRT", "PANTS", "DRESS"],
  },
  {
    key: "FASHION_SHOES",
    label: "Chaussures",
    description: "Baskets, sandales, bottes — avec pointure et couleur.",
    productTypes: ["SHOES"],
  },
  {
    key: "FASHION_BAGS",
    label: "Sacs & Bagages",
    description: "Sacs à main, sacs à dos, valises et accessoires.",
    productTypes: ["BAG", "FASHION_ACCESSORY"],
  },
  {
    key: "HOME_TEXTILE",
    label: "Linge de maison",
    description: "Rideaux, nappes, draps, serviettes.",
    productTypes: ["HOME_TEXTILE"],
  },
  {
    key: "HOME_FURNITURE",
    label: "Meubles & Décoration",
    description: "Meubles, étagères, objets de décoration.",
    productTypes: ["FURNITURE", "DECOR"],
  },
  {
    key: "GENERAL_RETAIL",
    label: "Commerce général",
    description: "Produits variés avec des quantités simples.",
    productTypes: ["GENERAL"],
  },
];

/* -------------------------------------------------------------------- client */

export const taxonomyApi = {
  /** Catalog profile of the active (or given) store. */
  profile: async (storeId?: string): Promise<CatalogProfile> => {
    try {
      const response = await apiClient.get("gestock/ai/catalog/profile", {
        headers: authHeaders(),
        params: storeId ? { storeId } : undefined,
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger le catalogue.");
    }
  },

  /** Product families + variants matching a name. */
  families: async ({
    query,
    storeId,
    includeVariants = true,
    limit = 20,
  }: {
    query?: string;
    storeId?: string;
    includeVariants?: boolean;
    limit?: number;
  }): Promise<{ families: ProductFamily[]; variants: ProductFamily[] }> => {
    try {
      const response = await apiClient.post(
        "gestock/ai/catalog/families",
        { query, storeId, includeVariants, limit },
        { headers: authHeaders() }
      );
      return {
        families: response.data?.families || [],
        variants: response.data?.variants || [],
      };
    } catch (error) {
      throw unwrap(error, "Recherche de produits impossible.");
    }
  },
};

export default taxonomyApi;
